import {
  COMPANY_NAME,
  NIGHT_BEFORE_SENDER_NAME,
  nightBeforeDefaults,
  nightBeforeEmail,
  nightBeforeSender,
  nightBeforeStartDefault,
  type Job,
  type NightBeforeAnswers,
  type NightBeforeFacts,
  type NightBeforeOrder,
} from "@roof-hawk/core";
import { useState } from "react";

import { Modal } from "../../components/ui";
import { t } from "../../i18n";
import { useAsync } from "../../lib/hooks";
import { fetchCompanySettings } from "../../lib/queries";
import { useSession } from "../../session";
import { ComposeEmail, type EmailDraft } from "../email/ComposeEmail";
import { useGmailAccount, useGmailSenders } from "../email/useGmailAccount";

function firstName(name: string | null | undefined): string {
  return (name ?? "").trim().split(/\s+/)[0] ?? "";
}

function sameDraft(a: EmailDraft, b: EmailDraft): boolean {
  return a.to === b.to && a.subject === b.subject && a.body === b.body;
}

/** The email as the rep left it, and the one it was written as. */
interface Draft {
  written: EmailDraft;
  current: EmailDraft;
}

/**
 * Whether "Write the email" builds it again from the answers.
 *
 * Yes when nothing was edited. When the rep edited the letter and an answer
 * has changed since, it asks — their edits would not say what the answers
 * now do. An edited letter whose answers stand is kept as it is.
 */
function rebuilds(draft: Draft | null, fresh: EmailDraft): boolean {
  if (!draft) return false;
  const edited = !sameDraft(draft.current, draft.written);
  if (!edited) return true;
  const answersChanged = !sameDraft(fresh, draft.written);
  return (
    answersChanged &&
    window.confirm(
      t("Build the email again from your answers?") +
        "\n\n" +
        t("Your changes to the email will be lost."),
    )
  );
}

/** What the job knows, for the letter. */
function gatherFacts(input: {
  job: Job;
  workOrders: readonly NightBeforeOrder[];
  /** Whether the letter leaves from Lysa's mailbox. */
  fromLysa: boolean;
  displayName: string;
  companyName: string | null | undefined;
}): NightBeforeFacts {
  const contact = input.job.contact ?? {};
  return {
    customerFirstName: contact.first_name ?? firstName(contact.full_name),
    // "contact either Sam or me": "me" is whoever the letter leaves from.
    senderFirstName: firstName(
      input.fromLysa ? NIGHT_BEFORE_SENDER_NAME : input.displayName,
    ),
    scheduledStart: nightBeforeStartDefault(input.workOrders),
    companyName: (input.companyName ?? "").trim() || COMPANY_NAME,
  };
}

/**
 * The night-before email to the customer (ADR 0313).
 *
 * Two steps, like the handoff (ADR 0280): first the few things the job cannot
 * know — the day, the two arrival windows, whether a dump trailer is coming,
 * who the project manager is and whether they stay all day, anything else —
 * then the letter, written from the answers and still editable. It goes to
 * the customer from Lysa's mailbox when the signed-in person may send from
 * it, and from their own otherwise.
 *
 * Nothing is saved but the email itself, which the send logs on the job like
 * any other letter to the customer.
 */
export function NightBeforeModal({
  job,
  workOrders,
  onClose,
}: {
  job: Job;
  workOrders: readonly NightBeforeOrder[];
  onClose: () => void;
}) {
  const { people, displayName } = useSession();
  const { account } = useGmailAccount();
  const {
    senders,
    own: ownSender,
    loading: sendersLoading,
  } = useGmailSenders();
  const company = useAsync(fetchCompanySettings, []);

  const lysa = nightBeforeSender(people);
  const lysaSender = senders.find((row) => row.profileId === lysa?.id);
  const contact = job.contact ?? {};
  const facts = gatherFacts({
    job,
    workOrders,
    fromLysa: Boolean(lysaSender),
    displayName,
    companyName: company.data?.name,
  });

  const [answers, setAnswers] = useState<NightBeforeAnswers>(() =>
    nightBeforeDefaults(facts),
  );
  const [writing, setWriting] = useState(false);
  // Kept across Back, so the rep's typing survives it.
  const [draft, setDraft] = useState<Draft | null>(null);
  // The mailbox the rep picked by hand, likewise kept across Back.
  const [pickedFrom, setPickedFrom] = useState<string | null>(null);

  const fresh = (): EmailDraft => ({
    to: contact.email ?? "",
    ...nightBeforeEmail(facts, answers),
  });

  const write = () => {
    // Until the mailboxes are known, nobody can say the letter is Lysa's: the
    // composer would open on the rep's own and the letter would say "me" of
    // the wrong person.
    if (sendersLoading) return;
    if (rebuilds(draft, fresh())) setDraft(null);
    setWriting(true);
  };

  if (writing) {
    const written = draft?.written ?? fresh();
    const opened = draft?.current ?? written;
    return (
      <ComposeEmail
        title={t("Night-before email to the customer")}
        subtitle={
          lysaSender
            ? t("From Lysa's mailbox, unless you pick another.")
            : t(
                "Lysa's mailbox is not yours to send from, so this leaves from your own.",
              )
        }
        to={opened.to}
        subject={opened.subject}
        body={opened.body}
        onDraftChange={(current) => setDraft({ written, current })}
        contact={contact}
        contactId={job.contact_id}
        jobId={job.id}
        connectedAs={account?.email_address ?? null}
        senders={senders}
        ownSender={ownSender}
        defaultFromProfileId={pickedFrom ?? lysaSender?.profileId ?? null}
        onFromChange={setPickedFrom}
        extraActions={
          <button className="btn-ghost" onClick={() => setWriting(false)}>
            {t("Back")}
          </button>
        }
        onClose={onClose}
      />
    );
  }

  return (
    <Questions
      answers={answers}
      onChange={(patch) => setAnswers((current) => ({ ...current, ...patch }))}
      onClose={onClose}
      onWrite={write}
      ready={!sendersLoading}
    />
  );
}

/** The few questions, each starting on what the job knows. */
function Questions({
  answers,
  onChange: set,
  onClose,
  onWrite,
  ready,
}: {
  answers: NightBeforeAnswers;
  onChange: (patch: Partial<NightBeforeAnswers>) => void;
  onClose: () => void;
  onWrite: () => void;
  /** False while the mailboxes it may leave from are still loading. */
  ready: boolean;
}) {
  return (
    <Modal
      title={t("Night-before email")}
      subtitle={t(
        "A few questions, then the email is written for you to look over and send.",
      )}
      onClose={onClose}
      actions={
        <>
          <button className="btn-ghost" onClick={onClose}>
            {t("Cancel")}
          </button>
          <button className="btn-primary" onClick={onWrite} disabled={!ready}>
            {t("Write the email")}
          </button>
        </>
      }
    >
      <label className="na-hint" htmlFor="nb-start">
        {t("What day does the work start?")}
      </label>
      <input
        id="nb-start"
        type="date"
        value={answers.start}
        onChange={(e) => set({ start: e.target.value })}
      />
      <label className="na-hint" htmlFor="nb-crew">
        {t('When does the crew arrive? (reads "between …")')}
      </label>
      <input
        id="nb-crew"
        value={answers.crewArrival}
        placeholder={t("e.g. 6:00 and 7:00 AM")}
        onChange={(e) => set({ crewArrival: e.target.value })}
      />
      <label className="na-hint" htmlFor="nb-materials">
        {t("The materials")}
      </label>
      <select
        id="nb-materials"
        value={answers.materials}
        onChange={(e) =>
          set({ materials: e.target.value as NightBeforeAnswers["materials"] })
        }
      >
        <option value="delivering">
          {t("Being delivered in the morning")}
        </option>
        <option value="on_site">{t("Already on site")}</option>
      </select>
      {answers.materials === "delivering" && (
        <>
          <label className="na-hint" htmlFor="nb-delivery">
            {t('When do the materials arrive? (reads "between …")')}
          </label>
          <input
            id="nb-delivery"
            value={answers.materialDelivery}
            placeholder={t("e.g. 8:00 and 9:00 AM")}
            onChange={(e) => set({ materialDelivery: e.target.value })}
          />
        </>
      )}
      <label className="na-hint" htmlFor="nb-trailer">
        {t("Dump trailer")}
      </label>
      <select
        id="nb-trailer"
        value={answers.dumpTrailer ? "yes" : "no"}
        onChange={(e) => set({ dumpTrailer: e.target.value === "yes" })}
      >
        <option value="yes">
          {t("Coming — the driveway stays clear for it")}
        </option>
        <option value="no">{t("No dump trailer on this job")}</option>
      </select>
      <label className="na-hint" htmlFor="nb-manager">
        {t("Who is the project manager?")}
      </label>
      <input
        id="nb-manager"
        value={answers.managerName}
        placeholder={t("e.g. Sam")}
        onChange={(e) => set({ managerName: e.target.value })}
      />
      <label className="na-hint" htmlFor="nb-all-day">
        {t("Are they on site all day?")}
      </label>
      <select
        id="nb-all-day"
        value={answers.managerAllDay}
        onChange={(e) =>
          set({
            managerAllDay: e.target
              .value as NightBeforeAnswers["managerAllDay"],
          })
        }
      >
        <option value="checking_in">
          {t("No — in constant communication with the crew")}
        </option>
        <option value="all_day">{t("Yes — on site throughout the day")}</option>
      </select>
      <label className="na-hint" htmlFor="nb-notes">
        {t("Anything else the customer should know?")}
      </label>
      <textarea
        id="nb-notes"
        style={{ minHeight: 70 }}
        value={answers.notes}
        placeholder={t(
          "e.g. Please keep the dog inside while the crew is on the roof.",
        )}
        onChange={(e) => set({ notes: e.target.value })}
      />
    </Modal>
  );
}
